import {useState, useCallback} from 'react';
import {DOC_STATUSES} from '../constants/documentCollection';
import {savePendingFiles} from '../utils/documentHelpers';
import {initiateUpload, initiateBatchUpload, uploadFileToS3, completeUpload} from '../api/documentCollectionApi';

const useDocumentUploads = ({
  token,
  uploadedFiles,
  insuranceHistoryDocs,
  setUploadedFile,
  removeUploadedFile,
  setDocumentStatus,
  setDocUploading,
}) => {
  const [singleHistoryFiles, setSingleHistoryFiles] = useState([]);
  const [batchErrors, setBatchErrors] = useState({});

  // Immediate upload — for single-file types (license, protection)
  const handleFileSelected = useCallback(async (documentType, file) => {
    setUploadedFile(documentType, {file});
    setDocUploading(documentType, true);
    try {
      const {uploadUrl, uploadId} = await initiateUpload(token, documentType, file);
      await uploadFileToS3(uploadUrl, file);
      await completeUpload(token, uploadId);
      savePendingFiles(token, documentType, [{name: file.name, uploadId}]);
      setDocumentStatus(documentType, DOC_STATUSES.IN_REVIEW, [file.name]);
      removeUploadedFile(documentType, 0);
    } catch (_err) {
      // Upload failed — file remains in state for retry
    } finally {
      setDocUploading(documentType, false);
    }
  }, [token, setUploadedFile, setDocUploading, setDocumentStatus, removeUploadedFile]);

  // Insurance history — add file to local state only (no immediate upload)
  const handleInsuranceFileSelected = useCallback((documentType, file) => {
    setUploadedFile(documentType, {file});
    setBatchErrors((prev) => ({...prev, [documentType]: null}));
  }, [setUploadedFile]);

  // Batch submit — initiate once with all files, upload each to S3, complete each
  const handleBatchSubmit = useCallback(async (documentType) => {
    const files = uploadedFiles[documentType] || [];
    if (files.length === 0) return;

    setDocUploading(documentType, true);
    setBatchErrors((prev) => ({...prev, [documentType]: null}));

    try {
      // Step 1: Initiate batch — one call with all files
      const rawFiles = files.map((f) => f.file);
      const {uploads} = await initiateBatchUpload(token, documentType, rawFiles);

      // Step 2: Upload each file to S3 sequentially
      for (let i = 0; i < uploads.length; i++) {
        await uploadFileToS3(uploads[i].uploadUrl, rawFiles[i]);
      }

      // Step 3: Complete each upload sequentially
      for (const upload of uploads) {
        await completeUpload(token, upload.uploadId);
      }

      // All succeeded — persist pending files and clear local files
      const fileNames = rawFiles.map((f) => f.name);
      savePendingFiles(token, documentType, uploads.map((u, i) => ({name: rawFiles[i].name, uploadId: u.uploadId})));
      setDocumentStatus(documentType, DOC_STATUSES.IN_REVIEW, fileNames);
      // Clear local files (they are now uploaded)
      files.forEach(() => removeUploadedFile(documentType, 0));
    } catch (_err) {
      setBatchErrors((prev) => ({...prev, [documentType]: 'שגיאה בהעלאת הקבצים, נסו שוב'}));
    } finally {
      setDocUploading(documentType, false);
    }
  }, [token, uploadedFiles, setDocUploading, setDocumentStatus, removeUploadedFile]);

  // Single insurance-history upload — one file for all periods
  const handleSingleHistoryFile = useCallback(async (file) => {
    setSingleHistoryFiles((prev) => [...prev, {file}]);
    const documentType = 'insurance_history';
    setDocUploading('single_history', true);
    try {
      const {uploadUrl, uploadId} = await initiateUpload(token, documentType, file);
      await uploadFileToS3(uploadUrl, file);
      await completeUpload(token, uploadId);
      insuranceHistoryDocs.forEach((doc) => {
        savePendingFiles(token, doc.id, [{name: file.name, uploadId}]);
        setDocumentStatus(doc.id, DOC_STATUSES.IN_REVIEW, [file.name]);
      });
      setSingleHistoryFiles([]);
    } catch (_err) {
      // Upload failed — file remains in state for retry
    } finally {
      setDocUploading('single_history', false);
    }
  }, [token, setDocUploading, setDocumentStatus, insuranceHistoryDocs]);

  return {
    batchErrors,
    singleHistoryFiles,
    handleFileSelected,
    handleInsuranceFileSelected,
    handleBatchSubmit,
    handleSingleHistoryFile,
  };
};

export default useDocumentUploads;
